/**
 * The report endpoints — slice 13's client surface.
 *
 *   createReportRun     POST /reports/runs                 starts a run; answers with the run, not a file
 *   fetchReportKinds    GET  /reports/kinds                the kinds a run can be recorded as here (slice 44)
 *   listReportRuns      GET  /reports/runs                 every run this workspace has, paginated
 *   downloadReportRun   GET  /reports/runs/:id/download    the bytes, under the tenant guard
 *
 * **There is no endpoint here that returns a spreadsheet from parameters, and
 * adding one would undo the slice.** The shape slice 13 exists to refuse is an
 * endpoint that streams a workbook built inline from a query string: it cannot
 * be scheduled later without being rewritten, because when a schedule fires
 * there is no request, no signed-in user and nowhere to put the file. So a
 * report is a *definition plus a run*, this app starts a run, and a person
 * clicking a button is one of two ways that happens — the nightly schedule is
 * the other, and it reaches the same generator through the same run record.
 * That is why {@link createReportRun} answers with a `ReportRun` whose status is
 * `queued` rather than with a download: the run exists the moment it is created,
 * whether or not the file does yet, which is exactly what a 201 means here.
 *
 * **Nothing in this file names a tenant, and there is no parameter that could.**
 * `createReportRunRequestSchema` has no `tenantId` — the API fills it from the
 * authenticated principal and RLS enforces it underneath — so a report holding
 * real contact data cannot be requested for somebody else's workspace by editing
 * a body. A run belonging to another tenant is unreachable by guessing its id
 * for the same reason: {@link downloadReportRun} takes an id and no tenant, and
 * the answer to a foreign id is the API's 404.
 *
 * **The download is fetched, not linked.** `nothing returns a local path or a
 * URL` is the storage port's own promise, so the bytes are only reachable
 * through the API — and an `<a href="/api/reports/…">` carries no `Authorization`
 * header, so it would get a 401 and save an error page as a `.xlsx`. That is the
 * same argument `apiBlob` was written for in slice 4, and it is why this file
 * has one function that returns a `Blob` and none that returns a link.
 *
 * **Both ends are checked against the shared contract**, as everywhere else: the
 * request is re-validated with `createReportRunRequestSchema` before it leaves
 * the browser, and every response is parsed with the shared read shape, so a
 * `failed` run that arrived carrying an artifact — or a `succeeded` one that
 * arrived without a file — is refused here rather than rendered as "no report".
 *
 * The route names below are the ones the API module of this slice registers,
 * following `credits.controller.ts`'s `@Controller('credits')` plus a noun. If
 * they ever move, this file is the only place in this app that has to change.
 */
import {
  createReportRunRequestSchema,
  reportKindsResponseSchema,
  reportRunListResponseSchema,
  reportRunResponseSchema,
  type CreateReportRunRequest,
  type ReportKind,
  type ReportRun,
  type ReportRunListQuery,
  type ReportRunListResponse,
} from '@wend/shared';

import { apiBlob, apiRequest } from './client';

/** Ids come from the API, but a path segment is never interpolated raw. */
function runPath(runId: string, suffix = ''): string {
  return `/reports/runs/${encodeURIComponent(runId)}${suffix}`;
}

/**
 * The kinds this deployment can record a run of — see `recordableReportKinds`.
 * The request panel offers only these.
 */
export async function fetchReportKinds(signal?: AbortSignal): Promise<ReportKind[]> {
  const response = await apiRequest({ path: '/reports/kinds', schema: reportKindsResponseSchema, signal });
  return response.kinds;
}

/**
 * Starts a run and returns it — `queued`, with no file yet.
 *
 * The window arrives fully resolved, which is deliberate: the browser is the one
 * caller that has a clock and a user in the same place, so "the month that just
 * ended" is turned into two instants *here*, at a moment that is recorded on the
 * run. A phrase sent to the server would be re-resolved against the server's
 * clock and would mean something different tomorrow.
 */
export async function createReportRun(request: CreateReportRunRequest): Promise<ReportRun> {
  const response = await apiRequest({
    path: '/reports/runs',
    method: 'POST',
    body: createReportRunRequestSchema.parse(request),
    schema: reportRunResponseSchema,
  });
  return response.run;
}

/**
 * Every run this workspace has, paginated. The order is the API's, and this app
 * does not re-sort — a screen that sorted a page it did not select would be
 * ordering a window onto the list rather than the list.
 *
 * **The screen passes no status filter**, and that is a decision rather than an
 * omission the contract already spells out: a list that hides failed runs by
 * default reports "no reports" for a workspace whose every run failed, which is
 * precisely the sentence slice 13 exists to stop being told.
 */
export async function listReportRuns(
  query: ReportRunListQuery,
  signal?: AbortSignal,
): Promise<ReportRunListResponse> {
  return apiRequest({
    path: '/reports/runs',
    query: {
      page: String(query.page),
      pageSize: String(query.pageSize),
      ...(query.kind === undefined ? {} : { kind: query.kind }),
      ...(query.status === undefined ? {} : { status: query.status }),
    },
    schema: reportRunListResponseSchema,
    signal,
  });
}

/**
 * The workbook's bytes.
 *
 * Only ever called for a run whose `reportRunArtifact()` is non-null — a run
 * that is queued, running or failed has no file, and asking for one is how a
 * failed run turns into a 404 that reads to a person as a broken button. The
 * caller owns the `Blob`; {@link saveBlobAs} in `lib/download.ts` is what turns
 * it into a saved file and revokes the object URL afterwards.
 */
export async function downloadReportRun(runId: string, signal?: AbortSignal): Promise<Blob> {
  return apiBlob(runPath(runId, '/download'), signal);
}
